import { AgsAuthResponseSchema } from "ags/integration/schema/ags-auth-response-schema";
import { AgsPickSchema, AgsSpinPublicStateSchema, AgsSpinResponseSchema, AgsResultSchema } from "ags/integration/schema/ags-spin-response-schema";
import { Gameplay } from "appworks/model/gameplay/gameplay";
import { Record } from "appworks/model/gameplay/records/record";
import { AuthRequestPayload } from "appworks/model/gameplay/requests/auth-request-payload";
import { builderManager } from "appworks/server/builders/builder-manager";
import { RecordBuilder } from "appworks/server/builders/record-builder";
import { Services } from "appworks/services/services";
import { GameiomConfigSchema } from "gameiom/schema/gameiom-config-schema";
import { FreespinRecord } from "slotworks/model/gameplay/records/freespin-record";
import { FreespinWinResult } from "slotworks/model/gameplay/records/results/freespin-win-result";
import { SpinRecord } from "slotworks/model/gameplay/records/spin-record";
import { slotModel } from "slotworks/model/slot-model";
import { SlotBetService } from "slotworks/services/bet/slot-bet-service";
import { agsRecordUtils } from "../ags-record-utils";
import { AgsRecordBuilder } from "./abstract-ags-record-builder";
import { AuthRecordBuilder } from "./auth-record-builder";
import { PickRecordBuilder } from "./pick-record-builder";
import { SpinRecordBuilder } from "./spin-record-builder";

export class RecoveryRecordBuilder extends AgsRecordBuilder implements RecordBuilder {
    public id: string = "RecoveryRecordBuilder";

    public filter(request: any, response: any): boolean {
        if (request instanceof AuthRequestPayload) {
            const authResponse = JSON.parse(response.data) as AgsAuthResponseSchema;
            const publicState = authResponse.publicState;
            return publicState.recoveredGame !== undefined;
        }
        return false;
    }

    public build(gameplay: Gameplay, request: any, response: any): Record[] {
        this.getAuthRecordBuilder().build(gameplay, request, response);

        gameplay.isRecovered = true;
        gameplay.startBalance = gameplay.balance;

        const authResponse = JSON.parse(response.data) as AgsAuthResponseSchema;
        const publicState = authResponse.publicState;
        const recoveredState = publicState.recoveredGame;

        if(authResponse.config == null) {
            throw(`Config is null in the recovery response.`);
        }

        this.updateBetModel(authResponse.config, recoveredState);

        const spinData = recoveredState.result;
        const records = this.processSpinData(spinData, response);

        const parentResult = spinData.parentResult;
        if(parentResult) {
            const parentSpinRecord = this.getSpinRecordBuilder().buildRecord(parentResult, false, parentResult, (authResponse as any) as AgsSpinResponseSchema);
            parentSpinRecord.results = parentSpinRecord.results.concat(builderManager.buildResults(parentSpinRecord, publicState));
            parentSpinRecord.grid = agsRecordUtils.convertreelsToSlotworksGrid(parentResult.reels);
            records.unshift(parentSpinRecord);
        }

        const dummyWins = this.getDummyWins(records, spinData);
        records.splice(this.getFirstFreespinIndex(records), 0, ...dummyWins);

        return records;
    }

    protected getFirstFreespinIndex(records: Record[]): number {
        let freespinIndex = -1;

        records.forEach((record, index) => {
            if (freespinIndex < 0 && record instanceof FreespinRecord) {
                freespinIndex = index;
            }
        });

        return freespinIndex;
    }

    // Correct for missing records with dummy records
    // const totalFreespinWin =
    // sumWinsFreeSpins: 5820
    protected getDummyWins(records: Record[], spinData: AgsResultSchema): Record[] {
        const dummyWins: Record[] = [];

        let totalTrackedFreespinCashWins = 0;
        let totalTrackedFreespinWins = 0;
        let totalTrackedFreespins = 0;
        records.forEach((record) => {
            if (record instanceof SpinRecord || record instanceof FreespinRecord) {
                totalTrackedFreespinWins += record.getFreespinsWon();
            }

            if (record instanceof FreespinRecord) {
                totalTrackedFreespins++;
                totalTrackedFreespinCashWins += record.sumWins;
            }
        });

        const untrackedFreespinCashWins = spinData.currentFeatureWin - totalTrackedFreespinCashWins;
        const untrackedFreeSpinWins = spinData.totalFreeSpins - totalTrackedFreespinWins;
        const untrackedFreeSpins = (spinData.totalFreeSpins - spinData.freeSpinsLeft) - totalTrackedFreespins;

        for (let dummyId = 0; dummyId < untrackedFreeSpins; dummyId++) {
            const dummyWin = new FreespinRecord();

            if (dummyId === 0) {
                const dummyFreespinWin = new FreespinWinResult();
                dummyFreespinWin.freespinsWon = untrackedFreeSpinWins;
                dummyWin.results = [dummyFreespinWin];
                dummyWin.sumWins = untrackedFreespinCashWins;
            }

            dummyWins.push(dummyWin);
        }

        return dummyWins;
    }

    private processSpinData(spinData: AgsResultSchema, response: AgsSpinResponseSchema): Record[] {
        if(spinData.action == "freespin") {
            return [this.getSpinRecordBuilder().buildRecord(spinData, true, spinData.parentResult, response)];
        }
        else if(spinData.action == "spin") {
            return [this.getSpinRecordBuilder().buildRecord(spinData, false, spinData.parentResult, response)];
        }
        return [];
    }

    protected processChildFeatures(
        childFeatures: AgsResultSchema[] | AgsPickSchema[],
        parentRecord: SpinRecord,
        rootSpin: AgsResultSchema,
        response: AgsSpinResponseSchema,
        parentComplete?: boolean
    ): Record[] {
        const records = [];

        if (childFeatures && childFeatures.length > 0) {
            for (const childFeature of childFeatures) {
                if (childFeature.type === "freeSpin") {

                    const freespin = childFeature as AgsResultSchema;
                    const freespinRecord = this.getSpinRecordBuilder().buildRecord(freespin, true, rootSpin, response);

                    freespinRecord.hasChildren = false;
                    records.push(freespinRecord);

                    if (freespin.childFeature && freespin.childFeature.length > 0) {
                        freespinRecord.hasChildren = true;
                        records.push(...this.processChildFeatures(freespin.childFeature, freespinRecord, rootSpin, response));
                    }
                } else if (["pick", "jackpotPick"].indexOf(childFeature.type) > -1 ) {
                    const record = this.getPickRecordBuilder().buildRecord(childFeature as AgsPickSchema, parentComplete);

                    if (record.complete) {
                        parentRecord.setBonusPlayed("pick");
                    }

                    records.push(record);
                }
            }
        }

        return records;
    }

    protected updateBetModel(
        config: GameiomConfigSchema,
        recovered: AgsSpinPublicStateSchema
    ): void {

        const { creditsPerLine, creditSizes } = slotModel.read().bet;
        creditsPerLine.currentValue = recovered.betAmount;

        slotModel.write({ bet: { creditsPerLine, creditSizes } });
        Services.get(SlotBetService).setCreditSize(recovered.denom);

        this.setCreditValues(config);
    }

    protected getSpinRecordBuilder() {
        return new SpinRecordBuilder();
    }

    protected getAuthRecordBuilder() {
        return new AuthRecordBuilder();
    }

    protected getPickRecordBuilder() {
        return new PickRecordBuilder();
    }
}
